import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import './ProductGrid.css';
import { showToast } from '../utils/toast';

interface Product {
  id: string;
  title: string;
  thumbnail: string;
  slug: string;
  created_at: string;
  view_count?: number;
}

interface ProductGridProps {
  creatorEmail?: string;
  mode?: 'store' | 'dashboard';
  onAnalyticsClick?: (productId: string) => void;
  onProductUpdate?: () => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ creatorEmail, mode = 'dashboard', onAnalyticsClick, onProductUpdate }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'deleted'>('active');

  useEffect(() => {
    fetchProducts();
  }, [creatorEmail, activeTab]);

  const fetchProducts = async () => {
    let query = supabase
      .from('digitalproducts')
      .select('id, title, thumbnail, slug, created_at, view_count');

    if (creatorEmail) {
      const { data: userData } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', creatorEmail)
        .single();
      
      if (userData) {
        query = query.eq('creator_id', userData.id);
      }
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      query = query.eq('creator_id', user.id);
    }

    // Fetch products based on the active tab
    query = query.eq('is_deleted', activeTab === 'deleted');

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching products:', error);
      return;
    }

    setProducts(data || []);
  };

  const copyToClipboard = (slug: string) => {
    const link = `${window.location.origin}/sales/${slug}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(slug);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleDelete = async (productId: string) => {
    // Create a custom styled confirmation dialog
    const confirmDelete = () => {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.innerHTML = `
          <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); display: flex; justify-content: center; align-items: center; z-index: 10000;">
            <div style="background: #1a1a1a; border-radius: 12px; padding: 2rem; max-width: 500px; width: 90%; border: 1px solid #333;">
              <div style="display: flex; justify-content: center; margin-bottom: 1.5rem;">
                <div style="width: 60px; height: 60px; border-radius: 50%; background: #ff4444; display: flex; justify-content: center; align-items: center;">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V14M12 17H12.01M10.29 5.88L4.18 6.67C3.04 6.82 2.15 7.74 2.03 8.87L1.05 18.74C0.92 20.02 1.92 21.08 3.2 21.08H20.8C22.08 21.08 23.08 20.02 22.95 18.74L21.97 8.87C21.85 7.74 20.96 6.82 19.82 6.67L13.71 5.88C12.63 5.74 11.37 5.74 10.29 5.88Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <h2 style="color: white; text-align: center; margin-bottom: 1rem; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">Delete Product</h2>
              <p style="color: #ddd; text-align: center; margin-bottom: 1.5rem; line-height: 1.6; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
                Are you sure you want to delete this product?<br><br>
                • The product will be removed from your store<br>
                • Sales data will be preserved for accounting<br>
              </p>
              <div style="display: flex; gap: 1rem; justify-content: center;">
                <button id="cancelBtn" style="padding: 0.75rem 1.5rem; border-radius: 8px; background: #2a2a2a; color: white; border: 1px solid #444; cursor: pointer; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-weight: 500; transition: all 0.2s ease;">Cancel</button>
                <button id="confirmBtn" style="padding: 0.75rem 1.5rem; border-radius: 8px; background: linear-gradient(135deg, #ff4444 0%, #cc3333 100%); color: white; border: none; cursor: pointer; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-weight: 500; transition: all 0.2s ease;">Delete</button>
              </div>
            </div>
          </div>
        `;
        
        document.body.appendChild(modal);
        
        const cancelBtn = modal.querySelector('#cancelBtn');
        const confirmBtn = modal.querySelector('#confirmBtn');
        
        const closeModal = () => {
          document.body.removeChild(modal);
        };
        
        cancelBtn?.addEventListener('click', () => {
          closeModal();
          resolve(false);
        });
        
        confirmBtn?.addEventListener('click', () => {
          closeModal();
          resolve(true);
        });
        
        // Add hover effects
        cancelBtn?.addEventListener('mouseenter', () => {
          if (cancelBtn) (cancelBtn as HTMLElement).style.background = '#3a3a3a';
        });
        
        cancelBtn?.addEventListener('mouseleave', () => {
          if (cancelBtn) (cancelBtn as HTMLElement).style.background = '#2a2a2a';
        });
        
        confirmBtn?.addEventListener('mouseenter', () => {
          if (confirmBtn) (confirmBtn as HTMLElement).style.background = 'linear-gradient(135deg, #ff5555 0%, #dd4444 100%)';
        });
        
        confirmBtn?.addEventListener('mouseleave', () => {
          if (confirmBtn) (confirmBtn as HTMLElement).style.background = 'linear-gradient(135deg, #ff4444 0%, #cc3333 100%)';
        });
      });
    };

    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }

    try {
      // Soft delete the product by setting is_deleted to true
      const { error: updateError } = await supabase
        .from('digitalproducts')
        .update({ is_deleted: true })
        .eq('id', productId);

      if (updateError) throw updateError;

      // Get the current user's analytics first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current analytics count
      const { data: analytics, error: analyticsError } = await supabase
        .from('creator_analytics')
        .select('total_digitalproducts')
        .eq('creator_id', user.id)
        .single();

      if (analyticsError) {
        console.error('Error fetching analytics:', analyticsError);
        throw analyticsError;
      }

      // Update creator_analytics with the correct count
      const newTotal = Math.max(0, (analytics?.total_digitalproducts || 1) - 1);
      const { error: updateAnalyticsError } = await supabase
        .from('creator_analytics')
        .update({ total_digitalproducts: newTotal })
        .eq('creator_id', user.id);

      if (updateAnalyticsError) {
        console.error('Error updating analytics:', updateAnalyticsError);
        // Don't throw here to avoid rolling back the product soft deletion
      }

      // Update local state
      setProducts(products.filter(product => product.id !== productId));
      showToast.success('Product deleted successfully');
      
      // Notify parent to refresh metrics
      if (onProductUpdate) onProductUpdate();
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast.error('Failed to delete product');
      if (error instanceof Error) {
        showToast.error(error.message);
      }
    }
  };

  const handleRecover = async (productId: string) => {
    try {
      // Recover the product by setting is_deleted to false
      const { error: updateError } = await supabase
        .from('digitalproducts')
        .update({ is_deleted: false })
        .eq('id', productId);

      if (updateError) throw updateError;

      // Get the current user's analytics
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current analytics count
      const { data: analytics, error: analyticsError } = await supabase
        .from('creator_analytics')
        .select('total_digitalproducts')
        .eq('creator_id', user.id)
        .single();

      if (analyticsError) {
        console.error('Error fetching analytics:', analyticsError);
        throw analyticsError;
      }

      // Update creator_analytics with the correct count (+1)
      const newTotal = (analytics?.total_digitalproducts || 0) + 1;
      const { error: updateAnalyticsError } = await supabase
        .from('creator_analytics')
        .update({ total_digitalproducts: newTotal })
        .eq('creator_id', user.id);

      if (updateAnalyticsError) {
        console.error('Error updating analytics:', updateAnalyticsError);
      }

      // Update local state
      setProducts(products.filter(product => product.id !== productId));
      showToast.success('Product recovered successfully');

      // Notify parent to refresh metrics
      if (onProductUpdate) onProductUpdate();
    } catch (error) {
      console.error('Error recovering product:', error);
      showToast.error('Failed to recover product');
    }
  };

  return (
    <div className="product-grid-container">
      {mode === 'dashboard' && (
        <div className="product-tabs">
          <button 
            className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Active Products
          </button>
          <button 
            className={`tab-btn ${activeTab === 'deleted' ? 'active' : ''}`}
            onClick={() => setActiveTab('deleted')}
          >
            Deleted Products
          </button>
        </div>
      )}

      {activeTab === 'deleted' && products.length > 0 && (
        <div className="deleted-warning-note">
          <p>⚠️ Note: Products in this section are scheduled for permanent deletion after 30 days if not recovered.</p>
        </div>
      )}

      <div className="products-grid">
        {products.map((product) => (
          <div key={product.id} className="product-card">
            <img
              src={product.thumbnail}
              alt={product.title}
              className="product-thumbnail"
              loading="lazy"
            />
            <div className="product-info">
              <h3>{product.title}</h3>
              <div className="product-actions">
                {activeTab === 'active' ? (
                  <>
                    <button
                      onClick={() => copyToClipboard(product.slug)}
                      className="copy-link-btn"
                    >
                      {copiedLink === product.slug ? 'Copied!' : 'Copy Link'}
                    </button>
                    <a
                      href={`/sales/${product.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-page-btn"
                    >
                      View Page
                    </a>
                    {mode === 'dashboard' && (
                      <>
                        <a
                          href={`/edit/${product.slug}`}
                          className="edit-btn"
                        >
                          Edit
                        </a>
                        <button
                          onClick={() => onAnalyticsClick && onAnalyticsClick(product.id)}
                          className="analytics-btn"
                        >
                          Analytics
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="delete-btn"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 7H20M10 11V17M14 11V17M5 7L6 19C6 20.1046 6.89543 21 8 21H16C17.1046 21 18 20.1046 18 19L19 7M9 7V4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Delete
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => handleRecover(product.id)}
                    className="recover-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 10H13C17.4183 10 21 13.5817 21 18V20M3 10L8 5M3 10L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Recover Product
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {products.length === 0 && (
        <div className="no-products-message">
          <p>{activeTab === 'active' ? 'No active products found.' : 'No deleted products found.'}</p>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
